import * as React from 'react';
import { useRef, useEffect, useCallback, useState } from 'react';
import { message } from 'antd';
import { MAPBOX_API_KEY } from '../../utils/ClientsideApiKeys';
import { ErrorBoundary } from '@sentry/react';
import { ServiceArea } from '../../types/serviceArea';
import {
	mapboxReverseGeocode,
	reverseGeocodePinLocalityLabel,
} from '../../thunks/mapbox_geocoding_thunks';
import {
	attachProgressiveGeoBoundariesMapbox,
	pickFinestSubAdminGeocodeFeature,
	updateGeoBoundariesSelectionHighlight,
	updateSearchPreviewHighlight,
} from './progressiveGeoBoundariesMap';
import { BoundaryRefinementRequest } from './progressiveGeoBoundariesMapCore';
import { PLACE_REVERSE_GEOCODE_TYPES } from '../edit_supplier_service_areas_form/constants';
import { getCountryFromArea, getStateFromArea } from '../edit_supplier_service_areas_form/utils';

const mapboxgl = require('mapbox-gl');

const CENTER_OF_UNITED_STATES: [number, number] = [-98.5795, 39.8283];
const DEFAULT_ZOOM = 3;

/** Mapbox Web Mercator: 360° longitude spans this many CSS px at zoom z: TILE * 2^z. With `renderWorldCopies: false`, clamp min zoom so one world is at least as wide as the map (no side gutters). */
const MAPBOX_WORLD_BASE_PX = 512;
/** North/south limit for Web Mercator; panning is clamped to one world (with `renderWorldCopies: false`). */
const MAPBOX_MERCATOR_LAT_MAX = 85.051129;

const MAPBOX_WORLD_MAX_BOUNDS: [[number, number], [number, number]] = [
	[-180, -MAPBOX_MERCATOR_LAT_MAX],
	[180, MAPBOX_MERCATOR_LAT_MAX],
];

function minZoomSingleWorldFillWidth(containerWidthPx: number): number {
	if (containerWidthPx == null || containerWidthPx < 1) {
		return -2;
	}
	const minZoomLevelFromWidth = Math.log(containerWidthPx / MAPBOX_WORLD_BASE_PX) / Math.LN2;
	return Math.max(-2, Math.min(minZoomLevelFromWidth + 0.01, 24));
}

function applyMinZoomForSingleWorldNoGutter(map: any) {
	if (!map || typeof map.getContainer !== 'function') {
		return;
	}
	try {
		map.resize();
		const mapContainerWidthPx = map.getContainer().clientWidth;
		const minZoomLevel = minZoomSingleWorldFillWidth(mapContainerWidthPx);
		map.setMinZoom(minZoomLevel);
		if (map.getZoom() < minZoomLevel) {
			map.setZoom(minZoomLevel);
		}
	} catch (ignoredError) {
		// ignore during teardown
	}
}

const serviceAreaFromPinLngLat = (lng: number, lat: number): ServiceArea => {
	const latStr = lat.toFixed(6);
	const lngStr = lng.toFixed(6);
	return {
		name: latStr + ', ' + lngStr,
		placeType: 'poi',
		center: [lng, lat],
		bbox: null,
	};
};

export interface CenterOnTarget {
	bbox?: [number, number, number, number];
	center?: [number, number];
	zoom?: number;
}

interface ServiceAreaMapProps {
	serviceAreas: ServiceArea[];
	height?: number | string;
	onMapClick?: (area: ServiceArea) => void;
	/**
	 * Progressive boundaries: replace a whole country or whole state with finer admin areas, excluding
	 * the clicked boundary (see `resolveBoundaryRefinementFromMapClick` in core).
	 */
	onApplyBoundaryRefinement?: (request: BoundaryRefinementRequest) => void;
	/**
	 * When the user picks a boundary that is already in the list (e.g. duplicate map/search pick),
	 * call this to remove it. Not used for progressive map hit-testing (refinement uses admin layers).
	 */
	onRemoveSelectedBoundary?: (area: ServiceArea) => void;
	/** When set without onMapClick, any click on the map canvas invokes this (e.g. open editor on preview). */
	onMapCanvasClick?: () => void;
	geocodeTypes?: string; // e.g. 'region' | 'district' | 'region,district'
	centerOn?: CenterOnTarget | null;
	/**
	 * Load ADM0 → ADM1 → ADM2 from hosted GeoJSON (WORLD-ADM0 + per-country files; see geoBoundaries download script).
	 * When true, map clicks use boundary selection instead of reverse geocoding.
	 */
	progressiveGeoBoundaries?: boolean;
	/** Called once after progressive boundary layers are attached (use for queryRenderedFeatures enrichment). */
	onProgressiveMapReady?: (map: any) => void;
	/**
	 * When true, map clicks add an exact WGS84 point (`placeType: poi`) instead of reverse-geocoding or admin boundaries.
	 */
	precisePinMode?: boolean;
	/**
	 * When true without progressive boundaries, map clicks reverse-geocode for Locality mode.
	 * Ignored when `progressiveGeoBoundaries` is true (localities use bundled ADM3 polygons when available).
	 */
	placePickMode?: boolean;
	/** Progressive map only: form selection mode (`country` | `region` | `district` | `place` | `pin`) for boundary hover/click priority. */
	boundarySelectionMode?: string;
	/**
	 * When false, selection pin markers and on-map name labels for region/county picks are not shown
	 * (boundary fills/outlines still update). Does not disable map boundary clicks — use omitting `onMapClick`
	 * for a read-only map. Default true.
	 */
	showSelectionMarkers?: boolean;
	/**
	 * Progressive map: outline only (gray) for the latest search dropdown pick — not added to
	 * `serviceAreas` until the user confirms on the map.
	 */
	searchPreviewServiceArea?: ServiceArea | null;
	/**
	 * When true, disables mouse-wheel zoom on the map. Use on preview maps embedded inside a
	 * scrollable page (e.g. the Service Areas card on the company detail page) so the user can
	 * scroll past the map instead of accidentally zooming it. The +/− buttons and click-drag
	 * panning remain functional. Default false (scroll-zoom enabled) for full-screen editors.
	 */
	disableScrollZoom?: boolean;
}

const ServiceAreaMapComponent = (props: ServiceAreaMapProps) => {
	const {
		serviceAreas,
		height,
		onMapClick,
		onMapCanvasClick,
		geocodeTypes,
		centerOn,
		progressiveGeoBoundaries,
		onProgressiveMapReady,
		precisePinMode,
		placePickMode,
		boundarySelectionMode,
		showSelectionMarkers,
		searchPreviewServiceArea,
		onApplyBoundaryRefinement,
		onRemoveSelectedBoundary,
		disableScrollZoom,
	} = props;
	const markersEnabled = showSelectionMarkers !== false;
	const searchPreviewServiceAreaRef = useRef<ServiceArea | null>(null);
	searchPreviewServiceAreaRef.current =
		searchPreviewServiceArea !== undefined && searchPreviewServiceArea !== null
			? searchPreviewServiceArea
			: null;

	const mapRef = useRef<any>(null);
	const precisePinModeRef = useRef(precisePinMode === true);
	precisePinModeRef.current = precisePinMode === true;
	const placePickModeRef = useRef(placePickMode === true);
	placePickModeRef.current = placePickMode === true;
	const boundarySelectionModeRef = useRef(boundarySelectionMode);
	boundarySelectionModeRef.current = boundarySelectionMode;
	const markersEnabledRef = useRef(markersEnabled);
	markersEnabledRef.current = markersEnabled;
	const serviceAreasRef = useRef<ServiceArea[]>(serviceAreas);
	serviceAreasRef.current = serviceAreas;
	const onProgressiveMapReadyRef = useRef(onProgressiveMapReady);
	onProgressiveMapReadyRef.current = onProgressiveMapReady;
	const mapContainerRef = useRef<HTMLDivElement>(null);
	const mapLoadedRef = useRef(false);
	const geocodeClickListenerBoundRef = useRef(false);
	const previewClickListenerBoundRef = useRef(false);
	const loadingLevelsRef = useRef<Set<string>>(new Set());
	const [boundaryLoading, setBoundaryLoading] = useState<string | null>(null);

	// Store latest callback refs so the map click handler always calls the current prop
	const onMapClickRef = useRef(onMapClick);
	onMapClickRef.current = onMapClick;
	const onMapCanvasClickRef = useRef(onMapCanvasClick);
	onMapCanvasClickRef.current = onMapCanvasClick;
	const onApplyBoundaryRefinementRef = useRef(onApplyBoundaryRefinement);
	onApplyBoundaryRefinementRef.current = onApplyBoundaryRefinement;
	const onRemoveSelectedBoundaryRef = useRef(onRemoveSelectedBoundary);
	onRemoveSelectedBoundaryRef.current = onRemoveSelectedBoundary;
	const geocodeTypesRef = useRef(geocodeTypes);
	geocodeTypesRef.current = geocodeTypes;
	const centerOnRef = useRef(centerOn);
	centerOnRef.current = centerOn;

	const handleZoomIn = useCallback(() => {
		if (mapRef.current) {
			mapRef.current.zoomIn();
		}
	}, []);

	const handleZoomOut = useCallback(() => {
		if (mapRef.current) {
			mapRef.current.zoomOut();
		}
	}, []);

	// Tab change does not fire map events; re-apply reference admin line opacity (Country tab hides
	// them) and refresh the persistent in-map zoom hint pill so it reflects the new tab's minimum
	// zoom requirement (or disappears if the new tab doesn't need one).
	useEffect(() => {
		if (!mapLoadedRef.current || !mapRef.current || !progressiveGeoBoundaries) {
			return;
		}
		const applier = mapRef.current._owApplyBoundaryReferenceLineOpacity;
		if (typeof applier === 'function') {
			applier();
		}
		const refreshTip = mapRef.current._owRefreshProgressiveBoundaryTooltipFromLastPointer;
		if (typeof refreshTip === 'function') {
			refreshTip();
		}
		const updateZoomHint = mapRef.current._owUpdateBoundaryZoomHint;
		if (typeof updateZoomHint === 'function') {
			updateZoomHint();
		}
	}, [boundarySelectionMode, progressiveGeoBoundaries]);

	// Mount / unmount
	useEffect(
		() => {
			let disposeProgressiveBoundaries: (() => void) | null = null;
			let resizeObserver: ResizeObserver | null = null;
			let onMapResizeForMinZoom: () => void;
			mapLoadedRef.current = false;
			mapboxgl.accessToken = MAPBOX_API_KEY;
			const map = new mapboxgl.Map({
				container: mapContainerRef.current,
				style: 'mapbox://styles/mapbox/light-v9',
				center: CENTER_OF_UNITED_STATES,
				zoom: DEFAULT_ZOOM,
				attributionControl: false,
				interactive: true,
				// Avoid repeating the world when zoomed out (Mercator wrap); +/− zoom stays on one copy
				renderWorldCopies: false,
				maxBounds: MAPBOX_WORLD_MAX_BOUNDS,
				// A stray double-click should not zoom in — users picking counties click rapidly, and
				// Mapbox's 300ms double-click delay defers single-click processing and fires an unwanted
				// zoomend (which re-runs progressive boundary refresh).
				doubleClickZoom: false,
				// No 3D pitch/rotate: Ctrl+drag (and right-click drag) used to tilt/rotate the globe,
				// which confused selection. Also disable the touch-pitch gesture for symmetry.
				dragRotate: false,
				pitchWithRotate: false,
				touchPitch: false,
				// Preview maps embedded inside a scrollable page disable scroll-zoom so the wheel
				// continues scrolling the page; users zoom via the +/− buttons on the map instead.
				scrollZoom: !disableScrollZoom,
			});
			mapRef.current = map;

			onMapResizeForMinZoom = () => applyMinZoomForSingleWorldNoGutter(map);
			map.on('resize', onMapResizeForMinZoom);

			map.on('load', () => {
				mapLoadedRef.current = true;

				// Apply any centerOn that arrived before the map finished loading
				const pendingCenterOn = centerOnRef.current;
				if (pendingCenterOn) {
					if (pendingCenterOn.bbox) {
						map.fitBounds(pendingCenterOn.bbox, { padding: 60, duration: 0, maxZoom: 8 });
					} else if (pendingCenterOn.center) {
						map.jumpTo({ center: pendingCenterOn.center, zoom: pendingCenterOn.zoom || 7 });
					}
				}

				if (progressiveGeoBoundaries) {
					disposeProgressiveBoundaries = attachProgressiveGeoBoundariesMapbox(map, {
						interactive: !!(
							onMapClickRef.current ||
							onMapCanvasClickRef.current ||
							precisePinModeRef.current
						),
						getServiceAreas: () => serviceAreasRef.current,
						getStateLabelFromArea: getStateFromArea,
						getCountryLabelFromArea: getCountryFromArea,
						getOnApplyBoundaryRefinement: () => onApplyBoundaryRefinementRef.current,
						getInteractionMode: () => {
							if (precisePinModeRef.current) {
								return 'pin_drop';
							}
							return 'boundaries';
						},
						getSelectionFormMode: () => {
							const m = boundarySelectionModeRef.current;
							return m != null && m !== '' ? m : 'district';
						},
						getOnPinSelect: () => {
							const cb = onMapClickRef.current;
							if (!cb) {
								return undefined;
							}
							return (lngLat: { lng: number; lat: number }) => {
								const base = serviceAreaFromPinLngLat(lngLat.lng, lngLat.lat);
								reverseGeocodePinLocalityLabel(lngLat.lng, lngLat.lat).then((label) => {
									if (label) {
										base.pinLabel = label;
									}
									cb(base);
								});
							};
						},
						getOnSelect: () => {
							const cb = onMapClickRef.current;
							if (!cb) {
								return undefined;
							}
							return (area: ServiceArea) => {
								const sid = area.boundaryShapeId;
								if (sid) {
									const list = serviceAreasRef.current;
									for (let i = 0; i < list.length; i++) {
										if (
											list[i].boundaryShapeId != null &&
											String(list[i].boundaryShapeId) === String(sid)
										) {
											const removeCb = onRemoveSelectedBoundaryRef.current;
											if (removeCb) {
												removeCb(area);
												return;
											}
											message.info('That area is already selected. Remove it from the list first.');
											return;
										}
									}
								}
								cb(area);
							};
						},
						onBoundaryLoadingChange: (isLoading, level) => {
							const levels = loadingLevelsRef.current;
							if (isLoading) {
								levels.add(level);
							} else {
								levels.delete(level);
								// Re-apply selection highlights after data arrives
								updateGeoBoundariesSelectionHighlight(map, serviceAreasRef.current, {
									showMarkers: markersEnabledRef.current,
									selectionFormMode:
										boundarySelectionModeRef.current != null &&
										boundarySelectionModeRef.current !== ''
											? boundarySelectionModeRef.current
											: 'district',
								});
								updateSearchPreviewHighlight(map, searchPreviewServiceAreaRef.current);
							}
							if (levels.size > 0) {
								// Show the finest level currently loading
								const label = levels.has('ADM2')
									? 'counties'
									: levels.has('ADM1')
									? 'states'
									: 'countries';
								setBoundaryLoading(label);
							} else {
								setBoundaryLoading(null);
							}
						},
					});
					if (onProgressiveMapReadyRef.current) {
						onProgressiveMapReadyRef.current(map);
					}
				} else if (onMapClickRef.current && !geocodeClickListenerBoundRef.current) {
					geocodeClickListenerBoundRef.current = true;
					map.getCanvas().style.cursor = 'crosshair';
					map.on('click', (e) => {
						const callback = onMapClickRef.current;
						if (!callback) return;
						const lng = e.lngLat.lng;
						const lat = e.lngLat.lat;
						if (precisePinModeRef.current) {
							const base = serviceAreaFromPinLngLat(lng, lat);
							reverseGeocodePinLocalityLabel(lng, lat).then((label) => {
								if (label) {
									base.pinLabel = label;
								}
								callback(base);
							});
							return;
						}
						if (placePickModeRef.current) {
							const ptypes = geocodeTypesRef.current || PLACE_REVERSE_GEOCODE_TYPES;
							mapboxReverseGeocode(lng, lat, ptypes).then((features) => {
								const f = pickFinestSubAdminGeocodeFeature(features as any[]);
								if (!f) {
									message.warning(
										'No place found at this location. Zoom in closer or try a nearby point.'
									);
									return;
								}
								const rawType = f.place_type && f.place_type[0];
								if (rawType === 'country' || rawType === 'region') {
									message.info(
										'Zoom in and click again to pick a city, neighborhood, county, or street-level area.'
									);
									return;
								}
								callback({
									name: f.place_name,
									placeType: 'place',
									center: f.center,
									bbox: f.bbox || null,
								});
							});
							return;
						}
						const types = geocodeTypesRef.current || 'region';
						mapboxReverseGeocode(lng, lat, types).then((features) => {
							if (features.length > 0) {
								const feature = features[0];
								const area: ServiceArea = {
									name: feature.place_name,
									placeType: feature.place_type[0],
									center: feature.center,
									bbox: feature.bbox || null,
								};
								callback(area);
							}
						});
					});
				} else if (onMapCanvasClick && !previewClickListenerBoundRef.current) {
					previewClickListenerBoundRef.current = true;
					map.getCanvas().style.cursor = 'pointer';
					map.on('click', onMapCanvasClick);
				}

				applyMinZoomForSingleWorldNoGutter(map);
			});

			const containerEl = map.getContainer();
			if (typeof ResizeObserver !== 'undefined' && containerEl) {
				resizeObserver = new ResizeObserver(() => {
					applyMinZoomForSingleWorldNoGutter(map);
				});
				resizeObserver.observe(containerEl);
			}

			setTimeout(() => {
				if (mapRef.current) {
					mapRef.current.resize();
					applyMinZoomForSingleWorldNoGutter(mapRef.current);
				}
			}, 350);

			return () => {
				map.off('resize', onMapResizeForMinZoom);
				if (resizeObserver) {
					resizeObserver.disconnect();
					resizeObserver = null;
				}
				if (disposeProgressiveBoundaries) {
					disposeProgressiveBoundaries();
					disposeProgressiveBoundaries = null;
				}
				if (mapRef.current) {
					mapRef.current.remove();
					mapRef.current = null;
				}
				mapLoadedRef.current = false;
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[progressiveGeoBoundaries]
	);

	// Highlight boundary polygons when progressive mode and serviceAreas change
	useEffect(() => {
		if (!mapLoadedRef.current || !mapRef.current) return;
		if (progressiveGeoBoundaries) {
			const modeForHighlight =
				boundarySelectionMode != null && boundarySelectionMode !== ''
					? boundarySelectionMode
					: 'district';
			updateGeoBoundariesSelectionHighlight(mapRef.current, serviceAreas, {
				showMarkers: markersEnabled,
				selectionFormMode: modeForHighlight,
			});
			const refreshTip = mapRef.current._owRefreshProgressiveBoundaryTooltipFromLastPointer;
			if (typeof refreshTip === 'function') {
				refreshTip();
			}
			// When serviceAreas change (e.g. after save), the progressive boundary system may
			// need to load ADM2 data that wasn't fetched at the current zoom level. Trigger a
			// progressive refresh so `refreshForView` re-evaluates which admin levels to load
			// based on the updated selection (e.g. newly saved counties).
			const progressiveRefresh = mapRef.current._owScheduleProgressiveRefresh;
			if (typeof progressiveRefresh === 'function') {
				progressiveRefresh();
			}
		}
	}, [serviceAreas, progressiveGeoBoundaries, markersEnabled, boundarySelectionMode]);

	useEffect(() => {
		if (!mapLoadedRef.current || !mapRef.current || !progressiveGeoBoundaries) {
			return;
		}
		updateSearchPreviewHighlight(
			mapRef.current,
			searchPreviewServiceArea !== undefined && searchPreviewServiceArea !== null
				? searchPreviewServiceArea
				: null
		);
	}, [searchPreviewServiceArea, progressiveGeoBoundaries]);

	// Update cursor when click handlers change
	useEffect(() => {
		if (!mapLoadedRef.current || !mapRef.current) return;
		const canvas = mapRef.current.getCanvas();
		if (progressiveGeoBoundaries) {
			if (precisePinMode) {
				canvas.style.cursor = 'crosshair';
			}
			// Non–pin progressive: `attachProgressiveGeoBoundariesMapbox` `mousemove` sets `pointer` / `''`.
		} else if (onMapClick) {
			canvas.style.cursor = 'crosshair';
		} else if (onMapCanvasClick) {
			canvas.style.cursor = 'pointer';
		} else {
			canvas.style.cursor = '';
		}
	}, [onMapClick, onMapCanvasClick, progressiveGeoBoundaries, precisePinMode]);

	// Center the map when centerOn changes
	useEffect(() => {
		if (!mapLoadedRef.current || !mapRef.current || !centerOn) return;
		if (centerOn.bbox) {
			mapRef.current.fitBounds(centerOn.bbox, { padding: 60, duration: 800, maxZoom: 8 });
		} else if (centerOn.center) {
			mapRef.current.flyTo({
				center: centerOn.center,
				zoom: centerOn.zoom || 7,
				duration: 800,
			});
		}
	}, [centerOn]);

	const zoomBtnClassName =
		'box-border h-[30px] w-[30px] cursor-pointer select-none border border-solid border-grey-5 bg-white p-0 text-center text-[18px] leading-[28px]';

	return (
		<div className="relative w-full" style={{ height: height || 280 }}>
			{progressiveGeoBoundaries ? (
				<div className="pointer-events-auto absolute bottom-2 left-2 z-[11] max-w-[85%] rounded bg-white/[0.92] px-2 py-1 text-[11px] leading-[1.3] text-grey-8">
					Boundary data:{' '}
					<a href="https://www.geoboundaries.org/" target="_blank" rel="noopener noreferrer">
						geoBoundaries
					</a>{' '}
					(CC BY 4.0). Modified for this application.
				</div>
			) : null}
			<div className="absolute inset-0 h-full w-full bg-grey-3" ref={mapContainerRef} />
			{boundaryLoading ? (
				<div className="pointer-events-none absolute inset-x-0 top-2.5 z-[12] flex justify-center">
					<div className="flex items-center gap-2 rounded-md bg-white/[0.92] px-4 py-1.5 text-[13px] text-grey-8 shadow-map-loading-overlay">
						<span className="inline-block h-3.5 w-3.5 animate-[ow-gb-spin_0.8s_linear_infinite] rounded-full border-2 border-solid border-grey-5 border-t-buyer" />
						Loading {boundaryLoading}...
					</div>
				</div>
			) : null}
			<style>{`@keyframes ow-gb-spin { to { transform: rotate(360deg); } }`}</style>
			<div className="absolute bottom-3 right-3 z-10 flex flex-col overflow-hidden rounded shadow-map-control-stack">
				<button type="button" className={zoomBtnClassName} onClick={handleZoomIn} title="Zoom in">
					+
				</button>
				<button
					type="button"
					className={zoomBtnClassName + ' border-t-0'}
					onClick={handleZoomOut}
					title="Zoom out"
				>
					-
				</button>
			</div>
		</div>
	);
};

const ServiceAreaMap = (props: ServiceAreaMapProps) => (
	<ErrorBoundary fallback={null}>
		<ServiceAreaMapComponent {...props} />
	</ErrorBoundary>
);

export default ServiceAreaMap;
